import React from "react";

interface BulletPoint {
  point: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  bullets?: string | BulletPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultBullets = [
  {
    point: "Light Absorption",
    description: "Chlorophyll molecules capture sunlight energy",
    icon: "☀️",
  },
  {
    point: "Water Splitting",
    description: "H2O molecules split into H+ and O2",
    icon: "💧",
  },
  {
    point: "Electron Transport",
    description: "Electrons flow through photosystems",
    icon: "⚡",
  },
  {
    point: "ATP Production",
    description: "Chemical energy is stored as ATP",
    icon: "🔋",
  },
  {
    point: "Carbon Fixation",
    description: "CO2 is converted to glucose",
    icon: "🌱",
  },
  {
    point: "Oxygen Release",
    description: "O2 is released as a byproduct",
    icon: "🌬️",
  },
];

const BulletSlide = {
  id: "bullet-slide",
  name: "Bullet Points",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Key Stages of Photosynthesis",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "Understanding the Process Step by Step",
    },
    {
      id: "bullets",
      label: "Bullets (JSON array of { point, description, icon })",
      type: "text",
      defaultValue: JSON.stringify(defaultBullets, null, 2),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue:
        "Each stage builds upon the previous one to complete the process.",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #15803d 0%, #16a34a 50%, #22c55e 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "Key Stages of Photosynthesis",
      subtitle = "Understanding the Process Step by Step",
      teacherNote = "Each stage builds upon the previous one to complete the process.",
      bgColor = "linear-gradient(135deg, #15803d 0%, #16a34a 50%, #22c55e 100%)",
      bullets = JSON.stringify(defaultBullets),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleBulletEdit = (index: number, field: string, value: string) => {
      let parsedBullets = defaultBullets;
      try {
        if (typeof bullets === "string") {
          parsedBullets = JSON.parse(bullets);
        }
      } catch (error) {
        console.warn("Error parsing bullets:", error);
      }

      const updatedBullets = [...parsedBullets];
      updatedBullets[index] = {
        ...updatedBullets[index],
        [field]: value,
      };

      handleEdit("bullets")(JSON.stringify(updatedBullets));
    };

    let parsedBullets = defaultBullets;
    try {
      if (typeof bullets === "string") {
        const parsed = JSON.parse(bullets);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedBullets = parsed;
        }
      } else if (Array.isArray(bullets) && bullets.length > 0) {
        parsedBullets = bullets as BulletPoint[];
      }
    } catch (error) {
      console.warn("Error parsing bullets, using defaults:", error);
    }

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full 
                        transform translate-x-1/3 -translate-y-1/2"
          />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-white/5 rounded-full" />
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleEdit("title")(e.currentTarget.textContent || "")
              }
              className="text-4xl font-black mb-3 text-green-100"
            >
              {title}
            </div>
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleEdit("subtitle")(e.currentTarget.textContent || "")
              }
              className="text-xl font-light text-green-100/90"
            >
              {subtitle}
            </div>
            <div className="w-32 h-1 mx-auto mt-4 bg-gradient-to-r from-green-300/50 to-transparent" />
          </div>

          {/* Bullets Grid */}
          <div className="flex-1 grid grid-cols-2 gap-6 mb-8">
            {parsedBullets.map((bullet, idx) => (
              <div
                key={idx}
                className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6
                          transform transition-all duration-300 hover:scale-105 hover:bg-white/20"
              >
                {/* Bullet Number */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                  {idx + 1}
                </div>

                {/* Content */}
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "icon",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="text-3xl group-hover:scale-110 transition-transform"
                  >
                    {bullet.icon}
                  </div>

                  {/* Text */}
                  <div>
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) =>
                        handleBulletEdit(
                          idx,
                          "point",
                          e.currentTarget.textContent || "",
                        )
                      }
                      className="text-lg font-bold mb-1 text-green-100"
                    >
                      {bullet.point}
                    </div>
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) =>
                        handleBulletEdit(
                          idx,
                          "description",
                          e.currentTarget.textContent || "",
                        )
                      }
                      className="text-sm text-green-100/80 leading-relaxed"
                    >
                      {bullet.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Teacher Note */}
          <div className="text-center">
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleEdit("teacherNote")(e.currentTarget.textContent || "")
              }
              className="text-sm text-green-100/70 italic"
            >
              {teacherNote}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default BulletSlide;
